import { useState, useEffect } from "react";

import { makeStyles, Typography, Box, Grid } from "@material-ui/core";
import { ReferenceArea, ReferenceLine, CartesianGrid } from "recharts";

import { api, getData } from "../../../../services/api";
import { NewPowerAssetsTable } from "./NewPowerAssetsTable";
import { GanttChart } from "../../../../components/charts/GanttChart";
import { COLORS } from "../../../../theme/colors";
import { NewPowerAssetsSuccessModal } from "./NewPowerAssetsSuccessModal";
import { NewPowerAssetsApprovalModal } from "./NewPowerAssetsApprovalModal";
import { CurrentDateCustomLabel } from "../operatingPowerAssetsTab/AssetDetail/CurrentDateCustomLabel";
import { getNewPowerAssetsGanttChartLegend } from "./getNewPowerAssetsGanttChartLegend";
import { RecommendedNewPowerAssetsGroupPeriod } from "./RecommendedNewPowerAssetsGroupPeriod";
import { recommendedPowerAssetsDataNormalizer } from "./recommendedPowerAssetsDataNormalizer";

const NOW_TIMESTAMP = new Date().getTime();
const tick = { fill: COLORS.darks[0][100] };

const tickFormatter = (x) => {
  const date = new Date(x);

  return `${date.getFullYear()}-${date.getMonth() + 1}`;
};

const useStyles = makeStyles((theme) => ({
  box: {
    padding: `${theme.spacing(3)}px 0`,
  },
  typography: {
    fontWeight: 700,
  },
  uppercase: {
    textTransform: "uppercase",
  },
  span: {
    fontStyle: "italic",
    fontWeight: 400,
  },
}));

export const RecommendedNewPowerAssetsTab = () => {
  const classes = useStyles();

  const initialData = {
    count: 0,
    previous: null,
    next: null,
    results: [],
  };

  const [ev, setEV] = useState(initialData);
  const [solar, setSolar] = useState(initialData);
  const [batteries, setBatteries] = useState(initialData);
  const [isOpen, setOpen] = useState(false);
  const [isOpenSuccessModal, setOpenSuccessModal] = useState(false);
  const [approveData, setApproveData] = useState([]);
  const [recommendedPowerAssetsGroups, setRecommendedPowerAssetsGroups] = useState([]);

  const lastDate = new Date(
    Math.max(
      ...recommendedPowerAssetsGroups.map((powerAssetGroup) =>
        new Date(powerAssetGroup["end_date"]).getTime()
      )
    )
  ).getTime();

  useEffect(() => {
    const getNewPowerAssets = (filter) => {
      const url = `/praas/v1/power-assets/list-by-user/${filter}`;
      return api.get(url);
    };

    getNewPowerAssets("?type=BATTERY").then((resp) => {
      const results = resp.data.results.map((item) => ({
        ...item,
        checked: false,
      }));

      setBatteries({
        ...resp.data,
        results,
      });
    });
    getNewPowerAssets("?type=EV").then((resp) => {
      const results = resp.data.results.map((item) => ({
        ...item,
        checked: false,
      }));

      setEV({
        ...resp.data,
        results,
      });
    });
    getNewPowerAssets("?type=SOLAR").then((resp) => {
      const results = resp.data.results.map((item) => ({
        ...item,
        checked: false,
      }));

      setSolar({
        ...resp.data,
        results,
      });
    });
  }, []);

  useEffect(() => {
    const approveData = []
      .concat(ev.results, solar.results, batteries.results)
      .filter((item) => item.checked);
    setApproveData(approveData);
  }, [ev.results, solar.results, batteries.results]);

  useEffect(() => {
    const url = "/praas/v1/power-assets/recommended-new-power-assets/";

    getData(url, (data) => {
      setRecommendedPowerAssetsGroups(data.groups);
    });
  }, []);

  return (
    <div>
      <Grid container className={classes.box}>
        <Grid item xs={12} style={{ height: 344, backgroundColor: COLORS.white }}>
          <GanttChart
            domain={["auto", lastDate]}
            xAxisKey="startDate"
            xAxisType="number"
            yAxisKey="yoffset"
            yAxisType="number"
            shape={RecommendedNewPowerAssetsGroupPeriod}
            data={recommendedPowerAssetsGroups}
            dataNormalizer={recommendedPowerAssetsDataNormalizer}
            tick={tick}
            tickFormatter={tickFormatter}
            backgroundComponents={[
              <CartesianGrid key="cartesian-grid" vertical={false} strokeDasharray={4} />,
              <ReferenceArea
                key="reference-area"
                x1={0}
                x2={NOW_TIMESTAMP}
                y1={0}
                y2={recommendedPowerAssetsGroups.length * 40}
                ifOverflow="hidden"
                fill={COLORS.gray[0]}
              />,
            ]}
            foregroundComponents={[
              <ReferenceLine
                key="reference-line"
                x={NOW_TIMESTAMP}
                stroke={COLORS.black}
                strokeDasharray={10}
                strokeWidth={1}
                label={<CurrentDateCustomLabel />}
              />,
              getNewPowerAssetsGanttChartLegend(recommendedPowerAssetsGroups),
            ]}
          />
        </Grid>
      </Grid>
      <Box className={classes.box}>
        <Typography className={classes.typography}>
          <span className={classes.uppercase}>Increase your power assets income </span> -{" "}
          <span className={classes.span}>
            Select new assets to install from the recommendation list below
          </span>
        </Typography>
      </Box>
      <Box>
        <NewPowerAssetsTable
          data={batteries.results}
          name="Best Fit for Batteries"
          type="battery"
          handleChangeResults={setBatteries}
          showActionButtons={true}
          total={approveData.length}
          setOpen={setOpen}
          id="new-power-assets-batteries"
        />
        <NewPowerAssetsTable
          data={ev.results}
          name="Best Fit for EV Charging"
          type="ev"
          handleChangeResults={setEV}
          id="new-power-assets-ev"
        />
        <NewPowerAssetsTable
          data={solar.results}
          name="Best Fit for Solar"
          type="solar"
          handleChangeResults={setSolar}
          id="new-power-assets-solar"
        />
      </Box>
      <NewPowerAssetsApprovalModal
        approveData={approveData}
        isOpen={isOpen}
        setOpen={setOpen}
        setOpenSuccessModal={setOpenSuccessModal}
      />
      <NewPowerAssetsSuccessModal isOpen={isOpenSuccessModal} setOpen={setOpenSuccessModal} />
    </div>
  );
};
